import React, { useMemo, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Settings from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import { Button } from "@mui/material";
import { useRouter } from "@tanstack/react-router";
import config from "@/config";
import { useNavigationUtils } from "@/utils/navigationHooks";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppSelector } from "@/redux/hooks";

interface UserInfoProps {
  onMenuItemClick?: () => void;
}

export default function UserInfo({ onMenuItemClick }: UserInfoProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const privyState = usePrivy();
  const router = useRouter();
  const { t } = useTranslation();

  const verify = useAppSelector((state) => state.verify);
  const { isVerified } = verify;
  const { user } = useMemo(
    () => ({
      user: privyState.user,
      logout: privyState.logout,
    }),
    [privyState.user]
  );

  const { handleLogout } = useNavigationUtils();

  const isVerifyPath = router.state.location.pathname === "/verify";
  const isNotFoundPath = router.state.location.pathname === "/not-found";
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose(); // Close UserInfo menu
    onMenuItemClick?.(); // Close mobile navigation menu
    router.navigate({ to: config.PATHNAME.USER_PROFILE });
  };

  const handleSettings = () => {
    handleClose(); // Close UserInfo menu
    onMenuItemClick?.(); // Close mobile navigation menu
    // Add your settings logic here
  };

  const handleLogoutClick = () => {
    handleClose(); // Close UserInfo menu
    onMenuItemClick?.(); // Close mobile navigation menu
    handleLogout();
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          "&:hover": {
            borderRadius: "18px",
          },
        }}
      >
        <Tooltip title="Account">
          <Button
            onClick={handleClick}
            size="small"
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            sx={{
              display: "flex",
              padding: 1,
              paddingLeft: 2,
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
                borderRadius: "18px",
              },
              transition: "border 0.3s ease",
            }}
          >
            <span className="text-white capitalize">
              {user?.wallet?.address
                ? `${user.wallet.address.slice(0, 4)}...${user.wallet.address.slice(-4)}`
                : "Not available"}
            </span>
            <KeyboardArrowDownIcon sx={{ color: "white" }} />
          </Button>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={handleProfile}
          disabled={isVerifyPath || isNotFoundPath}
        >
          <Avatar /> {t("navbar.menu.profile")}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSettings} disabled={isVerifyPath || isNotFoundPath}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          {t("navbar.menu.settings")}
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t("navbar.menu.logout")}
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
